import { Dispatch } from 'react';
import { AppState, MainContextActionTypes } from '@constants';
import { logger } from '@utils/logger';
import { verifyCouponShared } from '@utils/sharedCouponVerification';

/**
 * Validates an applied coupon against the current cart state
 * If the coupon is no longer valid, it clears the coupon and discount
 * If the discount amount changes, it updates it
 */
export async function validateCouponOnCartChange(state: AppState, dispatch: Dispatch<MainContextActionTypes>): Promise<void> {
  // Only validate if we have an applied coupon and cart items
  if (!state.couponCode || !state.cartItems || state.cartItems.length === 0) {
    return;
  }

  try {
    const result = await verifyCouponShared({
      coupon: state.couponCode,
      cartItems: state.cartItems,
      country: state.country,
      currency: state.currency,
      price: state.price,
      t: ((key: string) => key) as any // Simple fallback for translation - types will be handled by actual usage
    });

    if (!result.success) {
      // Coupon is no longer valid, clear it
      dispatch({ type: 'setCoupon', data: null });
      dispatch({ type: 'setCouponCode', data: null });
      dispatch({ type: 'setCouponError', data: null });
      dispatch({ type: 'setDiscountAmount', data: 0 });

      logger.warn(`Coupon ${state.couponCode} is no longer valid for current cart items:`, result.error);
    } else if (result.discountAmount !== state.discountAmount) {
      // Update discount amount if it changed
      dispatch({ type: 'setDiscountAmount', data: result.discountAmount || 0 });
    }
  } catch (error) {
    logger.error('Error validating coupon on cart change', error);
    // Clear coupon on error to be safe
    dispatch({ type: 'setCoupon', data: null });
    dispatch({ type: 'setCouponCode', data: null });
    dispatch({ type: 'setCouponError', data: null });
    dispatch({ type: 'setDiscountAmount', data: 0 });
  }
}
